import type { EsimPackage } from "./api";

export function formatMbAsDataLabel(mb?: number | null): string | null {
  if (mb == null || mb <= 0) return null;
  if (mb >= 1024) {
    const gb = mb / 1024;
    return Number.isInteger(gb) ? `${gb} GB` : `${gb.toFixed(1)} GB`;
  }
  return `${Math.round(mb)} MB`;
}

export function formatThrottleSpeed(kbps?: number | null): string | null {
  if (kbps == null || kbps <= 0) return null;
  if (kbps >= 1024) {
    const mbps = kbps / 1024;
    return Number.isInteger(mbps) ? `${mbps} Mbps` : `${mbps.toFixed(1)} Mbps`;
  }
  return `${kbps} kbps`;
}

export function getUnlimitedDataLabel(
  pkg: EsimPackage,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const highSpeed = formatMbAsDataLabel(pkg.highSpeedAllowanceMb);
  if (highSpeed) {
    return t("esim.unlimited_high_speed_to", { amount: highSpeed });
  }
  return t("esim.unlimited_fair_use");
}

export function getFairUsageTitle(
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  return t("esim.fair_usage_title");
}

/** Localized display title from structured fields (not the supplier packageName). */
export function getLocalizedPackageDisplayTitle(
  pkg: EsimPackage,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  // Catalog `dataAmount` is stored in MB (same as shop cards).
  const data = pkg.isUnlimited
    ? t("esim.unlimited")
    : formatMbAsDataLabel(pkg.dataAmount) || `${pkg.dataAmount} MB`;

  return t("esim.package_display_title", {
    data,
    duration: t("esim.days", { count: pkg.durationDays }),
  });
}

/**
 * Always build FUP copy from i18n + structured fields.
 * Do not use API `fairUsageSummary` — it is English-only from the backend.
 */
export function getFairUsageBody(
  pkg: EsimPackage,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (pkg.isUnlimited) {
    const highSpeed = formatMbAsDataLabel(pkg.highSpeedAllowanceMb);
    const speed = formatThrottleSpeed(pkg.throttleSpeedKbps);
    if (highSpeed && speed) {
      return t("esim.fup_unlimited_detail", {
        amount: highSpeed,
        speed,
      });
    }
    if (highSpeed) {
      return t("esim.fup_unlimited_amount_only", { amount: highSpeed });
    }
    if (speed) {
      return t("esim.fup_unlimited_speed_only", { speed });
    }
    return t("esim.fup_unlimited_generic");
  }

  const amount =
    formatMbAsDataLabel(
      pkg.highSpeedAllowanceMb && pkg.highSpeedAllowanceMb > 0
        ? pkg.highSpeedAllowanceMb
        : pkg.dataUnit?.toUpperCase() === "GB"
          ? pkg.dataAmount * 1024
          : pkg.dataAmount,
    ) || null;

  if (amount) {
    return t("esim.fup_fixed_detail", { amount });
  }
  return t("esim.fup_fixed_generic");
}

const isIsoCountryCode = (code?: string | null) =>
  /^[a-z]{2}$/i.test((code ?? "").trim());

export type CoveredCountry = { code: string; name: string };

export function getCoveredCountries(pkg: EsimPackage): CoveredCountry[] {
  const covered: CoveredCountry[] = [];
  const seen = new Set<string>();

  const add = (rawCode?: string | null, rawName?: string | null) => {
    const code = (rawCode ?? "").trim().toLowerCase();
    if (!isIsoCountryCode(code) || seen.has(code)) return;
    seen.add(code);
    covered.push({
      code,
      name: (rawName ?? "").trim() || code.toUpperCase(),
    });
  };

  const isLocal =
    String(pkg.geography ?? "").trim().toLowerCase() === "local";

  if (isLocal) {
    add(pkg.countryCode, pkg.countryName);
  } else {
    (pkg.signals ?? []).forEach((s) => add(s.code, s.name));
    if (covered.length === 0) {
      add(pkg.countryCode, pkg.countryName);
    }
  }

  return covered;
}

export function uniqueCoverageLabel(
  ...parts: Array<string | undefined | null>
): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of parts) {
    const trimmed = (part ?? "").trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out.join(" · ");
}

export function flagUrl(code: string): string {
  return `https://flagcdn.com/w80/${code.toLowerCase()}.png`;
}
